import { useCallback, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { debounce } from "lodash";
import { ApiConstant, AppConstant, KeyConstant, LangConstant } from "const";
import { ApiUtils, HumpUtils, StorageUtils, SystemUtils } from "utils";

const DEFAULT_FILTERS = {
  keyword: "",
  unitId: 0,
  departmentId: 0,
  page: 1,
};

export default function useAccountList({ groupId } = {}) {
  const { t: getLabel } = useTranslation();

  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  const query = useQuery({
    enabled: Boolean(groupId),
    refetchOnWindowFocus: false,
    keepPreviousData: true,
    queryKey: [ApiConstant.REACT_QUERY_KEYS.getAccountList, groupId],
    queryFn: async () => {
      const branchId = StorageUtils.getStoreData(KeyConstant.KEY_BRANCH_ID);
      const apiPayload = HumpUtils.decamelizeKeys({
        branchId,
        isIncludeUnit: true,
        isIncludeDepartment: true,
        isIncludePosition: true,
      });

      const response = await ApiUtils.createApiWithToken(false).get(ApiConstant.GET_LIST_USERS, apiPayload);
      const responseData = response.data || {};
      return HumpUtils.camelizeKeys(responseData.data || []);
    },
    onError: () => {
      SystemUtils.appendNotification(getLabel(LangConstant.TXT_UPDATE_ERROR), "error");
    },
  });

  const allUsers = query.data || [];
  const keywordLower = (filters.keyword || "").toLowerCase().trim();

  // Filter out inactive / pending / refused accounts and apply search keyword, unit & department filters
  const filteredUsers = useMemo(() => {
    return allUsers.filter(u => {
      // Keep active and pending (not logged in yet) accounts, exclude disabled/refused
      const isNotDisabled = (
        u.state !== 0 &&
        u.branchAccountState !== 0 &&
        u.branchAccountState !== 2 &&
        u.branchAccountStatus !== 0
      );
      if (!isNotDisabled) return false;

      // Filter by unitId
      if (filters.unitId && String(filters.unitId) !== "0") {
        if (String(u.unitId) !== String(filters.unitId)) return false;
      }

      // Filter by departmentId
      if (filters.departmentId && String(filters.departmentId) !== "0") {
        if (String(u.departmentId) !== String(filters.departmentId)) return false;
      }

      // Filter by search keyword if present
      if (!keywordLower) return true;
      const nameMatch = (u.name || "").toLowerCase().includes(keywordLower);
      const phoneMatch = (u.phone || "").includes(keywordLower);
      return nameMatch || phoneMatch;
    });
  }, [allUsers, keywordLower, filters.unitId, filters.departmentId]);

  const pageSize = AppConstant.PAGE_SIZE;
  const totalElements = filteredUsers.length;
  const totalPages = Math.ceil(totalElements / pageSize) || 1;

  // Paginated list of active users
  const accounts = useMemo(() => {
    const start = (filters.page - 1) * pageSize;
    return filteredUsers.slice(start, start + pageSize);
  }, [filteredUsers, filters.page, pageSize]);

  // Debounced search — resets to page 1
  const handleSearch = useCallback(
    debounce(value => {
      setFilters(prev => ({ ...prev, keyword: value, page: 1 }));
    }, AppConstant.INPUT_DEBOUNCE_TIME),
    [],
  );

  const handlePageChange = page => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleFilterUnit = unitId => {
    setFilters(prev => ({ ...prev, unitId, departmentId: 0, page: 1 }));
  };

  const handleFilterDepartment = departmentId => {
    setFilters(prev => ({ ...prev, departmentId, page: 1 }));
  };

  const handleLoadMore = () => {
    setFilters(prev => ({ ...prev, page: prev.page + 1 }));
  };

  const handleResetQuery = () => {
    if (filters.page === 1 && filters.keyword === "") {
      query.refetch();
    } else {
      setFilters(DEFAULT_FILTERS);
    }
  };

  return {
    accounts,
    totalPages,
    totalElements,
    filters,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    handleSearch,
    handlePageChange,
    handleFilterUnit,
    handleFilterDepartment,
    handleLoadMore,
    handleResetQuery,
  };
}
